import axios, { AxiosError } from "axios";
import cx from "classnames";
import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import {
  Button,
  IPrimaryTertiaryButtonProps,
  ISecondaryButtonProps,
  getErrorPopupTexts,
} from "component-library";
import { dataTestIds } from "data-testids";

import { IBreakdown } from "shared/models/breakdown";
import { AccessType } from "shared/models/user";
import { MapDispatchToProps } from "shared/types/utilities";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { hidePopup, showPopup } from "store/ui/ui.actions";
import { HidePopup, PopupType, ShowPopup } from "store/ui/ui.types";

import { IPhoto } from "models/api";
import { INudge, ISuggestion } from "models/suggestion";

import getErrorPopupType from "helpers/getErrorPopupType";
import agentsService, {
  GetAgencies,
  IAgencyPermission,
} from "shared/services/api/agents/agentsService";
import suggestionsService from "services/api/suggestions/suggestionsService";
import {
  DeleteSuggestions,
  EditSuggestions,
  GetNudge,
  GetSuggestions,
  IMakeSuggestionRequestParams,
  MakeNudge,
  MakeSuggestions,
} from "services/api/suggestions/suggestionsService.types";

import {
  IBreakdownViewOnDataLoaded,
  IBreakdownViewRegisterRole,
  IBreakdownViewRole,
} from "components/BreakdownView/BreakdownView";
import FullAccessPerformer from "components/SuggestionsViewPerformer/FullAccessPerformer/FullAccessPerformer";
import { NudgeAccessPerformer } from "components/SuggestionsViewPerformer/NudgeAccessPerformer/NudgeAccessPerformer";
import {
  getPatch,
  IRoleSuggestionsViewTexts,
  prepareTexts,
} from "components/SuggestionsViewPerformer/SuggestionsViewPerformer.helpers";

import "./SuggestionsViewPerformer.scss";

export interface ISuggestionsViewPerformerLoadedData {
  agencyList?: IAgencyPermission[];
  suggestion?: ISuggestion;
  nudge?: INudge | undefined;
}

export interface ISuggestionsViewPerformerActions {
  showPopup: ShowPopup;
  hidePopup: HidePopup;
}

export interface ISuggestionsViewPerformerStoreProps {
  texts: IRoleSuggestionsViewTexts;
  getSuggestions: GetSuggestions;
  makeSuggestions: MakeSuggestions;
  deleteSuggestions: DeleteSuggestions;
  editSuggestions: EditSuggestions;
  getNudge: GetNudge;
  makeNudge: MakeNudge;
  getAgencies: GetAgencies;
}

export interface ISuggestionsViewPerformerProps
  extends ISuggestionsViewPerformerStoreProps,
    ISuggestionsViewPerformerActions {
  role: IBreakdownViewRole;
  breakdown: IBreakdown;
  userAccessType: AccessType;
  isRoleClosed: boolean;
  isPrintView?: boolean;
  reportConcernButton: React.ReactNode;
  registerRole?: (data: IBreakdownViewRegisterRole) => void;
  onDataLoaded?: (data: IBreakdownViewOnDataLoaded) => void;
  isRoleCast?: boolean | null;
}

export interface ISuggestionsViewPerformerState {
  isLoading: boolean;
  agencyList: IAgencyPermission[];
  suggestion?: ISuggestion;
  nudge?: INudge | undefined;
}

export interface IViewPerformerButtonProps {
  primary?: IPrimaryTertiaryButtonProps;
  secondary?: ISecondaryButtonProps;
}

export class SuggestionsViewPerformer extends React.Component<
  ISuggestionsViewPerformerProps,
  ISuggestionsViewPerformerState
> {
  public requestCancellation = axios.CancelToken.source();

  public readonly state: Readonly<ISuggestionsViewPerformerState> = {
    isLoading: true,
    agencyList: [],
  };

  public componentDidMount() {
    const { userAccessType } = this.props;

    if (userAccessType === AccessType.ReadOnly) {
      this.setState({ isLoading: false });
    } else if (userAccessType === AccessType.Full) {
      void this.getSuggestion();
    } else {
      void this.getNudge();
    }
  }

  public componentWillUnmount() {
    this.requestCancellation.cancel?.();
  }

  public render() {
    const {
      userAccessType,
      isRoleClosed,
      texts,
      getAgencies,
      isPrintView,
      isRoleCast,
    } = this.props;
    const { suggestion, isLoading, nudge } = this.state;

    if (isLoading) {
      return this.renderLoadingStub();
    }

    switch (userAccessType) {
      case AccessType.ReadOnly:
        return this.renderReadOnlyAccessPerformer();
      case AccessType.Nudge:
        return (
          <NudgeAccessPerformer
            isRoleClosed={isRoleClosed}
            isRoleCast={isRoleCast}
            renderTitle={this.renderTitle}
            renderButtons={this.renderButtons}
            onNudge={this.handleMakeNudge}
            texts={texts}
            nudge={nudge}
            getAgencies={getAgencies}
            handleError={this.handleError}
            isPrintView={isPrintView}
          />
        );
      case AccessType.Full:
        return (
          <FullAccessPerformer
            isRoleClosed={isRoleClosed}
            isRoleCast={isRoleCast}
            renderTitle={this.renderTitle}
            renderButtons={this.renderButtons}
            onMakeSuggestion={this.handleMakeSuggestions}
            onDeleteSuggestion={this.handleDeleteSuggestions}
            onEditSuggestion={this.handleEditSuggestions}
            texts={texts}
            suggestion={suggestion}
            handleError={this.handleError}
            isPrintView={isPrintView}
          />
        );
      default:
        return null;
    }
  }

  private renderTitle = () => (
    <h3 className="c-suggestions-view-performer__title">
      {this.props.texts.makeSuggestionLabelTitle}
    </h3>
  );

  private renderButtons = (buttonsProps: IViewPerformerButtonProps) => (
    <section className="c-suggestions-view-performer__buttons-set g-col-lg-12 g-inner-grid">
      <div className="g-col-lg-6 g-col-md-4 c-suggestions-view-performer__report-concerns">
        {this.props.reportConcernButton}
      </div>
      <div className="g-col-lg-3 g-col-md-4 c-suggestions-view-performer__secondary-btn">
        {buttonsProps?.secondary && <Button {...buttonsProps.secondary} />}
      </div>
      <div className="g-col-lg-3 g-col-md-4">
        {buttonsProps?.primary && <Button {...buttonsProps.primary} />}
      </div>
    </section>
  );

  private renderReadOnlyAccessPerformer = () => (
    <section
      className={cx("c-suggestions-view-performer", {
        "c-suggestions-view-performer--print": this.props.isPrintView,
      })}
    >
      <div className="c-suggestions-view-performer__content g-bg-default">
        {this.renderTitle()}
        <p className="c-suggestions-view-performer__read-access">
          {this.props.texts.makeSuggestionMessageReadAccess}
        </p>
      </div>
      {this.renderButtons({})}
    </section>
  );

  private renderLoadingStub = () => (
    <section className="c-suggestions-view-performer">
      {!this.props.isRoleClosed && (
        <div className="c-suggestions-view-performer__content g-bg-default">
          {this.renderTitle()}
          <p
            className="c-suggestions-view-performer__read-access"
            data-testid={
              dataTestIds.nuJobsApp["BreakdownDetails.suggestionsLoading.text"]
            }
          >
            loading...
          </p>
        </div>
      )}
      {this.renderButtons({})}
    </section>
  );

  private handleMakeSuggestions = (photo: string, comment: string) => {
    const { makeSuggestions, breakdown, role } = this.props;
    const suggestion: IMakeSuggestionRequestParams = {
      photo,
      comment,
      cduserid: breakdown.production.castingDirectorId,
    };

    this.toggleSuggestionMode(() =>
      makeSuggestions(breakdown.id, role.id, [suggestion])
    );
  };

  private handleMakeNudge = (agencyId: string) => {
    const { makeNudge, breakdown, role } = this.props;

    this.toggleNudgeMode(() => makeNudge(breakdown.id, role.id, agencyId));
  };

  private handleEditSuggestions = (photo: IPhoto, comment: string) => {
    const { editSuggestions, breakdown, role } = this.props;
    const suggestion = this.state.suggestion!;

    if (
      photo.fileName === suggestion.photo.fileName &&
      comment === suggestion.comment
    ) {
      return;
    }

    const patch = getPatch(photo.id, comment, suggestion.id);

    this.toggleSuggestionMode(() =>
      editSuggestions(breakdown.id, role.id, patch)
    );
  };

  private handleDeleteSuggestions = async () => {
    const { deleteSuggestions, breakdown, role } = this.props;
    const { suggestion } = this.state;

    this.setState({ isLoading: true });

    try {
      await deleteSuggestions(breakdown.id, role.id, [suggestion!.id]);

      const nextState = { isLoading: false, suggestion: undefined };

      this.setState(nextState);
      this.props.onDataLoaded?.({ id: role.id, loadedData: nextState });
    } catch (e) {
      this.handleError(e);
      this.setState({ isLoading: false });
    }
  };

  private toggleSuggestionMode(handler: () => void) {
    this.setState({ isLoading: true });

    try {
      handler();
      void this.getSuggestion();
    } catch (e) {
      this.handleError(e);
      this.setState({ isLoading: false });
    }
  }

  private toggleNudgeMode(handler: () => void) {
    this.setState({ isLoading: true });

    try {
      handler();
      void this.getNudge();
    } catch (e) {
      this.handleError(e);
      this.setState({ isLoading: false });
    }
  }

  private getNudge = async (): Promise<void> => {
    if (this.props.registerRole) {
      const args: IBreakdownViewRegisterRole = {
        id: this.props.role.id,
        callback: (data: ISuggestionsViewPerformerLoadedData) => {
          this.setState({
            nudge: data.nudge ?? this.state.nudge,
            isLoading: false,
          });
        },
      };

      this.props.registerRole(args);

      return;
    }

    const { breakdown, role, getNudge } = this.props;

    this.setState({ isLoading: true });
    this.requestCancellation.cancel?.();
    this.requestCancellation = axios.CancelToken.source();

    try {
      const nudge = await getNudge(
        breakdown.id,
        role.id,
        this.requestCancellation.token
      );
      const nextState = { nudge };

      this.setState(nextState);
      this.props.onDataLoaded?.({ id: role.id, loadedData: nextState });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  private getSuggestion = async (): Promise<void> => {
    if (this.props.registerRole) {
      const args: IBreakdownViewRegisterRole = {
        id: this.props.role.id,
        callback: (data: ISuggestionsViewPerformerLoadedData) => {
          this.setState({
            suggestion: data.suggestion ?? this.state.suggestion,
            isLoading: false,
          });
        },
      };
      this.props.registerRole(args);

      return;
    }

    const { role, getSuggestions } = this.props;

    this.setState({ isLoading: true });
    this.requestCancellation.cancel?.();
    this.requestCancellation = axios.CancelToken.source();

    try {
      const suggestions = await getSuggestions(role.id);
      const nextState = { suggestion: suggestions[0] };

      this.setState(nextState);
      this.props.onDataLoaded?.({ id: role.id, loadedData: nextState });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  readonly handleError = (e: AxiosError) => {
    this.props.showPopup({
      type: PopupType.ErrorPopup,
      traceID: e?.response?.headers?.["x-trace-identifier"],
      data: {
        ...getErrorPopupTexts()[getErrorPopupType()],
        hidePopup: this.props.hidePopup,
      },
    });
  };
}

const mapDispatchToProps: MapDispatchToProps<ISuggestionsViewPerformerActions> =
  {
    showPopup,
    hidePopup,
  };

const mapStateToProps = (
  state: IApplicationState
): ISuggestionsViewPerformerStoreProps => {
  const page = state.translation.pages[PageKey.Role]!;

  return {
    texts: prepareTexts(page),
    getSuggestions: (...args) => suggestionsService.getSuggestions(...args),
    makeSuggestions: (...args) => suggestionsService.makeSuggestions(...args),
    deleteSuggestions: (...args) =>
      suggestionsService.deleteSuggestions(...args),
    editSuggestions: (...args) => suggestionsService.editSuggestions(...args),
    getNudge: (...args) => suggestionsService.getNudge(...args),
    getAgencies: (...args) => agentsService.getAgencies(...args),
    makeNudge: (...args) => suggestionsService.makeNudge(...args),
  };
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  SuggestionsViewPerformer
);
